import { ThemeProvider, useTheme } from '@mui/material/styles';
import { PropsWithChildren, useMemo } from 'react';

import { TEXT_NOTE_DEFAULT_FONT_SIZE } from '@/styles/tokens/fonts';

const TEXT_NOTE_TEXT_PRIMARY_FONT_SIZE = `${TEXT_NOTE_DEFAULT_FONT_SIZE}px`;
const TEXT_NOTE_TEXT_PRIMARY_LINE_HEIGHT = '1rem'; // 16px

/**
 * Pins MUI's `text-primary` typography variant to the text note's base font
 * size for its subtree. Mirrors `PresentationTypographyProvider` — both exist
 * so the surrounding `TextEditor`'s rendered `<p>` size matches the value the
 * `SlideEditorFontSizeDropdown` is told is the "default" via its
 * `defaultFontSize` prop. Without this alignment, the dropdown's
 * "size === default → unwrap" decision lands at a size other than the visible
 * base, which makes nearby sizes look identical to it in the rendered note.
 */
export function TextNoteTypographyProvider({ children }: PropsWithChildren) {
  const theme = useTheme();

  const noteTheme = useMemo(() => {
    const baseTextPrimary = theme.typography['body-s'];
    return {
      ...theme,
      typography: {
        ...theme.typography,
        'body-s': {
          ...baseTextPrimary,
          fontSize: TEXT_NOTE_TEXT_PRIMARY_FONT_SIZE,
          lineHeight: TEXT_NOTE_TEXT_PRIMARY_LINE_HEIGHT,
        },
      },
    };
  }, [theme]);

  return <ThemeProvider theme={noteTheme}>{children}</ThemeProvider>;
}
